/**
 * `GET /api/address-book` — read-only listing of the seller's address_book
 * rows. Server filters `deleted_at IS NULL` and orders `is_main DESC,
 * created_at DESC`.
 *
 * Schema scope: widened twice from the original WS8.6 banner pin.
 *
 *  1. WS8.6 → +`id`, +`country` for the shared ship-from origin resolver
 *     (`hooks/use-shipping-origin.ts`) so create + edit flows agree on
 *     "what country is this listing shipping from?".
 *  2. Detail fields (`first_name`/`last_name`/`address`/`apartment`/`city`/
 *     `state`/`postal`/`phone`/`email`) for the buyer-side `ShippingForm`
 *     preselect and the seller-side settings `AddressBookSection` CRUD —
 *     both previously declared parallel local `SavedAddress`/`Address`
 *     interfaces and read raw JSON. Same SoT, all consumers now share one
 *     type.
 *
 * Nullability mirrors the prod `address_book` table columns:
 *  - NOT NULL → `id`, `is_main`, `country`, `address`, `city`, `postal`
 *  - nullable → `label` (legacy rows may pre-date the NOT NULL constraint),
 *    `first_name`, `last_name`, `apartment`, `state`, `phone`, `email`,
 *    `deleted_at`
 *
 * @see app/api/address-book/route.ts — handler
 * @see lib/address-book/list-addresses.ts — canonical GET builder
 * @see lib/address-book/upsert-address.ts — canonical POST/PUT builder
 * @see lib/address-book/set-main-address.ts — canonical PATCH builder
 * @see lib/address-book/delete-address.ts — canonical DELETE builder
 * @see hooks/use-address-book.ts — shared FSM data hook
 * @see hooks/use-shipping-origin.ts — single ship-from resolver
 * @see components/shipping-settings/auto-inferred-address-banner.tsx — banner consumer
 * @see .tondocs/shippo/PLAN.md §WS8.6.5 — Seller dashboard prompt
 */

import { z } from "zod";
import { pinned } from "./pin";

export interface AddressBookRow {
  id: string;
  label: string | null;
  is_main: boolean;
  country: string;
  deleted_at: string | null;
  first_name: string | null;
  last_name: string | null;
  address: string;
  apartment: string | null;
  city: string;
  state: string | null;
  postal: string;
  phone: string | null;
  email: string | null;
}

export const AddressBookRowSchema = pinned<AddressBookRow>()(
  z.object({
    id: z.string(),
    label: z.string().nullable(),
    is_main: z.boolean(),
    country: z.string(),
    deleted_at: z.string().nullable(),
    first_name: z.string().nullable(),
    last_name: z.string().nullable(),
    address: z.string(),
    apartment: z.string().nullable(),
    city: z.string(),
    state: z.string().nullable(),
    postal: z.string(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
  }),
);

export const AddressBookListResponseSchema = pinned<AddressBookRow[]>()(
  z.array(AddressBookRowSchema),
);

export type AddressBookListResponse = z.infer<
  typeof AddressBookListResponseSchema
>;
